import { Tooltip } from '@commenda/kakashi';
import { ThumbsDown, ThumbsUp } from 'lucide-react';

import {
  CommendaAiFeedbackRating,
  type CommendaAiSavedMessageFeedback,
} from '@commenda/api/commenda-ai/types';
import { cn } from '@commenda/utils';

import { ChatIconButton } from './ChatIconButton';
import { MessageFeedbackModal } from './MessageFeedbackModal';
import { useCommendaAiMessageFeedback } from './useCommendaAiMessageFeedback';

interface MessageFeedbackActionsProps {
  conversationId: string;
  /** Assistant message row the rating attaches to. Also the id its eval trace hangs off. */
  messageId: string;
  /** The verdict already saved on the server, so a reload keeps the thumb filled and the modal
   * prefilled. */
  initialFeedback?: CommendaAiSavedMessageFeedback | null;
  /** Fires after a rating persists, so the conversation-level feedback cache can revalidate. */
  onRatingSaved?: () => void;
}

/** Filled background plus brand foreground marks the rating the reader chose, so the click has a
 * visible result even before the modal appears. */
const SELECTED_CLASS_NAMES = 'bg-bg-tertiary text-fg-brand';

export function MessageFeedbackActions({
  conversationId,
  messageId,
  initialFeedback = null,
  onRatingSaved,
}: MessageFeedbackActionsProps) {
  const {
    displayedRating,
    savedFeedback,
    openModalRating,
    isSubmittingFeedback,
    feedbackError,
    rateMessage,
    closeFeedbackModal,
    submitFeedbackDetails,
  } = useCommendaAiMessageFeedback(conversationId, messageId, {
    initialFeedback,
    onRatingSaved,
  });

  // Prefill only when the open thumb matches the saved verdict: the other polarity has its own
  // category list, so it always opens fresh.
  const initialDetails =
    savedFeedback && savedFeedback.rating === openModalRating
      ? {
          category: savedFeedback.category ?? undefined,
          comment: savedFeedback.comment ?? undefined,
        }
      : undefined;

  return (
    <>
      <Tooltip content="Good response" asChild>
        <ChatIconButton
          aria-label="Good response"
          aria-pressed={displayedRating === CommendaAiFeedbackRating.UP}
          className={cn(
            displayedRating === CommendaAiFeedbackRating.UP &&
              SELECTED_CLASS_NAMES
          )}
          onClick={() => rateMessage(CommendaAiFeedbackRating.UP)}
          icon={
            <ThumbsUp
              className="size-4"
              aria-hidden="true"
              fill={
                displayedRating === CommendaAiFeedbackRating.UP
                  ? 'currentColor'
                  : 'none'
              }
            />
          }
        />
      </Tooltip>
      <Tooltip content="Bad response" asChild>
        <ChatIconButton
          aria-label="Bad response"
          aria-pressed={displayedRating === CommendaAiFeedbackRating.DOWN}
          className={cn(
            displayedRating === CommendaAiFeedbackRating.DOWN &&
              SELECTED_CLASS_NAMES
          )}
          onClick={() => rateMessage(CommendaAiFeedbackRating.DOWN)}
          icon={
            <ThumbsDown
              className="size-4"
              aria-hidden="true"
              fill={
                displayedRating === CommendaAiFeedbackRating.DOWN
                  ? 'currentColor'
                  : 'none'
              }
            />
          }
        />
      </Tooltip>
      {/* Keyed per open so the modal's category/comment state resets for each rating. */}
      <MessageFeedbackModal
        key={openModalRating ?? 'closed'}
        rating={openModalRating}
        initialDetails={initialDetails}
        isSubmitting={isSubmittingFeedback}
        error={feedbackError}
        onCancel={closeFeedbackModal}
        onSubmit={submitFeedbackDetails}
      />
    </>
  );
}
